import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  makeStyles,
  Typography,
  Box
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import SyncContactModal from "../SyncContactModal";

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: "100%"
  },
  submitButton: {
    marginLeft: theme.spacing(1)
  }
}));

const TicketClassificationDialog = ({ open, onClose, onSubmit, ticketId }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [observation, setObservation] = useState("");
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  
  // Estados para verificação de CRM
  const [checkingContact, setCheckingContact] = useState(false);
  const [contactData, setContactData] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [crmCheckComplete, setCrmCheckComplete] = useState(true); // Inicia como true
  const [crmNotFound, setCrmNotFound] = useState(false);

  useEffect(() => {
    if (open) {
      console.log("[TicketClassification] Modal aberta, ticketId:", ticketId);
      // Reset form
      setSelectedTypeId("");
      setSelectedCategoryId("");
      setObservation("");
      setSearchValue(null);
      setCrmNotFound(false);
      setShowSyncModal(false);
      
      // Verificar contato e buscar tipos
      checkContactBeforeClassification();
      fetchClassificationTypes();
    }
  }, [open]);

  const checkContactBeforeClassification = async () => {
    if (!ticketId) {
      console.log("[TicketClassification] ticketId não definido, abortando verificação");
      return;
    }

    console.log("[TicketClassification] Iniciando verificação do contato para ticketId:", ticketId);

    try {
      const { data: ticket } = await api.get(`/tickets/${ticketId}`);
      
      console.log("[TicketClassification] Ticket retornado:", {
        ticketId: ticket?.id,
        contactId: ticket?.contact?.id,
        contactNumber: ticket?.contact?.number,
        codDevedor: ticket?.contact?.codDevedor,
        sinistro: ticket?.contact?.sinistro
      });
      
      if (ticket && ticket.contact) {
        setContactData(ticket.contact);
        
        // Se codDevedor já existe, liberar classificação imediatamente
        if (ticket.contact.codDevedor) {
          console.log("[TicketClassification] ✅ Contato JÁ POSSUI codDevedor:", ticket.contact.codDevedor);
          console.log("[TicketClassification] Liberando classificação sem buscar CRM");
          setCrmCheckComplete(true);
          return;
        }
        
        console.log("[TicketClassification] ⚠️ Contato SEM codDevedor, iniciando busca no CRM...");
        // Se codDevedor for null, buscar no CRM
        setCheckingContact(true);
        setCrmCheckComplete(false);
        
        try {
          console.log("[TicketClassification] 🔍 Fazendo POST /crm/search-debtor com número:", ticket.contact.number);
          const { data: crmResponse } = await api.post("/crm/search-debtor", {
            contactNumber: ticket.contact.number
          });

          console.log("[TicketClassification] Resposta do CRM:", {
            success: crmResponse.success,
            totalRegistros: crmResponse.total_registros,
            qtdData: crmResponse.data?.length
          });

          if (crmResponse.success && crmResponse.data && crmResponse.data.length > 0) {
            console.log("[TicketClassification] ✅ Dados encontrados no CRM, abrindo modal de sincronização");
            // Encontrou dados no CRM, mostrar modal de sincronização
            setShowSyncModal(true);
          } else {
            console.log("[TicketClassification] ❌ Nenhum dado encontrado no CRM");
            // Não encontrou no CRM, mostrar botão para ignorar
            setCrmNotFound(true);
          }
        } catch (err) {
          console.error("[TicketClassification] ❌ Erro ao buscar no CRM:", err);
          // Em caso de erro, permitir continuar
          setCrmNotFound(true);
        } finally {
          console.log("[TicketClassification] Finalizando verificação de CRM");
          setCheckingContact(false);
        }
      }
    } catch (err) {
      console.error("[TicketClassification] ❌ Erro ao verificar contato:", err);
      toastError(err);
      setCrmCheckComplete(true);
    }
  };

  useEffect(() => {
    if (selectedTypeId) {
      const selectedType = types.find(t => t.id === selectedTypeId);
      const newCategories = selectedType?.categories || [];
      setCategories(newCategories);
      // Só resetar categoria se ela não existir nas novas categorias
      if (selectedCategoryId && !newCategories.find(c => c.id === selectedCategoryId)) {
        setSelectedCategoryId("");
      }
    } else {
      setCategories([]);
      setSelectedCategoryId("");
    }
  }, [selectedTypeId, types]);

  useEffect(() => {
    // Criar lista flat de todas as categorias com referência ao tipo
    if (types.length > 0) {
      const flatCategories = [];
      types.forEach(type => {
        if (type.categories && type.categories.length > 0) {
          type.categories.forEach(category => {
            flatCategories.push({
              ...category,
              typeName: type.name,
              typeId: type.id,
              typeColor: type.color,
              label: `${type.name} → ${category.name}`
            });
          });
        }
      });
      setAllCategories(flatCategories);
    }
  }, [types]);

  const fetchClassificationTypes = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/classification-types");
      setTypes(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedTypeId || !selectedCategoryId) {
      toast.error(i18n.t("ticketClassification.errors.requiredFields"));
      return;
    }

    const classificationData = {
      typeId: selectedTypeId,
      categoryId: selectedCategoryId,
      observation: observation.trim()
    };

    onSubmit(classificationData);
    onClose();
  };

  const handleClose = () => {
    console.log("[TicketClassification] Fechando modal de classificação");
    setSelectedTypeId("");
    setSelectedCategoryId("");
    setObservation("");
    setSearchValue(null);
    setContactData(null);
    setCrmCheckComplete(false);
    setCrmNotFound(false);
    setShowSyncModal(false);
    onClose();
  };

  const handleSyncContact = async (contactId, syncData) => {
    console.log("[TicketClassification] handleSyncContact chamado:", { contactId, syncData });
    try {
      await api.put(`/contacts/${contactId}`, syncData);
      toast.success("Contato sincronizado com sucesso!");
      console.log("[TicketClassification] Contato sincronizado, fechando modal de sync");
      setShowSyncModal(false);
      setCrmCheckComplete(true);
      
      // Atualizar dados do contato
      const { data: updatedContact } = await api.get(`/contacts/${contactId}`);
      console.log("[TicketClassification] Contato atualizado:", {
        id: updatedContact.id,
        codDevedor: updatedContact.codDevedor,
        sinistro: updatedContact.sinistro
      });
      setContactData(updatedContact);
    } catch (err) {
      console.error("[TicketClassification] Erro ao sincronizar contato:", err);
      toastError(err);
    }
  };

  const handleSkipSync = () => {
    console.log("[TicketClassification] Usuário optou por continuar sem sincronizar");
    setCrmNotFound(false);
    setCrmCheckComplete(true);
  };

  const handleSearchChange = (event, newValue) => {
    setSearchValue(newValue);
    if (newValue) {
      // Definir automaticamente tipo e categoria
      const selectedType = types.find(t => t.id === newValue.typeId);
      setCategories(selectedType?.categories || []);
      setSelectedTypeId(newValue.typeId);
      // Aguardar próximo tick para definir categoria após categories serem atualizadas
      setTimeout(() => {
        setSelectedCategoryId(newValue.id);
      }, 0);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("ticketClassification.dialog.title")}</DialogTitle>
        <DialogContent dividers>
          {checkingContact ? (
            <Box textAlign="center" py={3}>
              <CircularProgress />
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                Verificando dados do contato...
              </Typography>
            </Box>
          ) : crmNotFound ? (
            <Box textAlign="center" py={3}>
              <Typography variant="h6" gutterBottom>
                Contato não encontrado no CRM
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Não foi possível encontrar dados deste contato no sistema CRM.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Deseja continuar com a classificação sem sincronizar?
              </Typography>
              <Box mt={3}>
                <Button onClick={handleClose} color="default" style={{ marginRight: 8 }}>
                  Cancelar
                </Button>
                <Button onClick={handleSkipSync} color="primary" variant="contained">
                  Continuar sem sincronizar
                </Button>
              </Box>
            </Box>
          ) : !crmCheckComplete ? (
            <Box textAlign="center" py={3}>
              <CircularProgress />
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                Aguardando sincronização...
              </Typography>
            </Box>
          ) : loading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <CircularProgress />
            </div>
          ) : types.length === 0 ? (
            <Typography color="textSecondary">
              {i18n.t("ticketClassification.dialog.noTypesConfigured")}
            </Typography>
          ) : (
            <>
              {/* Campo de busca rápida */}
              <FormControl variant="outlined" className={classes.formControl}>
                <Autocomplete
                  options={allCategories}
                  getOptionLabel={(option) => option.label || ""}
                  value={searchValue}
                  onChange={handleSearchChange}
                  renderOption={(option) => (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          backgroundColor: option.typeColor,
                          marginRight: "8px"
                        }}
                      />
                      <span>{option.label}</span>
                    </div>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Busca rápida de classificação"
                      placeholder="Digite para buscar..."
                      variant="outlined"
                    />
                  )}
                  noOptionsText="Nenhuma classificação encontrada"
                />
              </FormControl>

              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="type-label">
                  {i18n.t("ticketClassification.dialog.type")}
                </InputLabel>
                <Select
                  labelId="type-label"
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value)}
                  label={i18n.t("ticketClassification.dialog.type")}
                  required
                >
                  {types.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      <span
                        style={{
                          display: "inline-block",
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          backgroundColor: type.color,
                          marginRight: "8px"
                        }}
                      />
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedTypeId && (
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="category-label">
                    {i18n.t("ticketClassification.dialog.category")}
                  </InputLabel>
                  <Select
                    labelId="category-label"
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    label={i18n.t("ticketClassification.dialog.category")}
                    required
                    disabled={categories.length === 0}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl variant="outlined" className={classes.formControl}>
                <TextField
                  label={i18n.t("ticketClassification.dialog.observation")}
                  multiline
                  rows={4}
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  variant="outlined"
                  placeholder={i18n.t("ticketClassification.dialog.observationPlaceholder")}
                />
              </FormControl>
            </>
          )}
        </DialogContent>
        {crmCheckComplete && !checkingContact && (
          <DialogActions>
            <Button onClick={handleClose} color="default">
              {i18n.t("ticketClassification.dialog.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              className={classes.submitButton}
              disabled={!selectedTypeId || !selectedCategoryId || loading}
            >
              {i18n.t("ticketClassification.dialog.confirm")}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {contactData && !contactData.codDevedor && (
        <SyncContactModal
          open={showSyncModal}
          onClose={() => setShowSyncModal(false)}
          contactNumber={contactData.number}
          contactId={contactData.id}
          codDevedor={contactData.codDevedor}
          onSync={handleSyncContact}
        />
      )}
    </>
  );
};

export default TicketClassificationDialog;
